import type { JmapMethodCall, JmapRequest, JmapResponse } from "./types";
import { DEFAULT_USING } from "./types";
import { recordMetric } from "@/lib/observability/metrics";
import { logJmap } from "./errors";

export type JmapClientOptions = {
  /** Browser: routes through /api/jmap. Server: direct URL + basic auth. */
  mode: "proxy" | "direct";
  apiUrl?: string;
  email?: string;
  password?: string;
};

export class JmapClient {
  constructor(private readonly options: JmapClientOptions) {}

  async call(methodCalls: JmapMethodCall[], using: string[] = DEFAULT_USING): Promise<JmapResponse> {
    const body: JmapRequest = { using, methodCalls };
    logJmap("call payload", body);
    const start = typeof performance !== "undefined" ? performance.now() : 0;
    const methodName = methodCalls[0]?.[0] ?? "unknown";
    try {
      if (this.options.mode === "proxy") {
        const res = await fetch("/api/jmap", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const err = (await res.json().catch(() => ({}))) as { error?: string };
          recordMetric("jmap.error", 1, { method: methodName, status: String(res.status) });
          throw new Error(err.error ?? `JMAP proxy error ${res.status}`);
        }
        const json = (await res.json()) as JmapResponse;
        logJmap("call response", json);
        if (start) recordMetric("jmap.latency_ms", performance.now() - start, { method: methodName });
        return json;
      }
      const { apiUrl, email, password } = this.options;
      if (!apiUrl || !email || !password) {
        throw new Error("Direct JMAP requires apiUrl, email, password");
      }
      const auth = btoa(`${email}:${password}`);
      const res = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${auth}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        recordMetric("jmap.error", 1, { method: methodName, status: String(res.status) });
        throw new Error(`JMAP error ${res.status}`);
      }
      const json = (await res.json()) as JmapResponse;
      logJmap("call response", json);
      if (start) recordMetric("jmap.latency_ms", performance.now() - start, { method: methodName });
      return json;
    } catch (e) {
      if (!(e instanceof Error && e.message.includes("JMAP"))) {
        recordMetric("jmap.error", 1, { method: methodName });
      }
      throw e;
    }
  }

  async echo(): Promise<boolean> {
    const res = await this.call([["Core/echo", { arg: "originmail" }, "e0"]]);
    const block = res.methodResponses.find(([m]) => m === "Core/echo")?.[1];
    return block?.arg === "originmail";
  }
}

/** Browser singleton — always proxy mode (sovereign: credentials never in client). */
let browserClient: JmapClient | null = null;

export function getJmapClient(): JmapClient {
  if (!browserClient) {
    browserClient = new JmapClient({ mode: "proxy" });
  }
  return browserClient;
}

export { block, JmapCallError, findMethodResponse } from "./errors";
